import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { faEnvelope, faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    getEngageDomains,
    getEngageSenders,
    resendSenderVerificationEmail as resendSenderVerificationEmailApi,
} from 'api/engage';
import { DeleteSenderDialog } from 'assets/js/pages/engage/components/DomainSettings/DeleteSenderDialog';
import * as DropdownMenu from 'components/dropdown-menu';
import { GivebutterFormProvider, SubmitButton } from 'components/form';
import { useGivebutterForm } from 'components/form/useGivebutterForm';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { EngageDomain, EngageSender } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

const SenderSettings = () => {
    const { accountId } = useCurrentAccount();
    const [isDeletingSender, setIsDeletingSender] = useState(false);
    const [selectedSender, setSelectedSender] = useState<EngageSender>();

    const { data: engageSenders } = useSuspenseQuery({
        queryKey: accountQueryKeys.engageSenders(accountId),
        queryFn: () => getEngageSenders(accountId),
        select: (response): Array<EngageSender> => {
            return response.data.data;
        },
    });

    const { data: engageDomains } = useSuspenseQuery({
        queryKey: accountQueryKeys.engageDomains(accountId),
        queryFn: () => getEngageDomains(accountId),
        select: (response): Array<EngageDomain> => {
            return response.data.data;
        },
    });

    return (
        <>
            <div className="mb-4 rounded-xl border border-gray-200 bg-white shadow-sm">
                <div className="flex items-center justify-between border-b border-b-gray-200 px-6 py-4">
                    <span className="text-lg font-medium">Email senders</span>
                    {!!engageDomains?.length && !!engageSenders?.length && (
                        <Link to={`/accounts/${accountId}/engage/settings/senders/new`}>
                            <Button variant="outline" color="neutral" size="sm">
                                <FontAwesomeIcon icon={faPlus} /> Add New
                            </Button>
                        </Link>
                    )}
                </div>

                {!engageDomains?.length && (
                    <div className="p-4">
                        You need to{' '}
                        <Link to={`/accounts/${accountId}/engage/settings/domains/new`}>
                            add a domain
                        </Link>{' '}
                        before you can create senders.
                    </div>
                )}

                {!!engageDomains?.length && !!engageSenders?.length && (
                    <>
                        <div className="grid grid-cols-12 border-b border-gray-200 bg-gray-50 text-xs font-medium">
                            <div className="col-span-3 px-6 py-1.5">Sender name</div>
                            <div className="col-span-6 px-6 py-1.5">Email</div>
                            <div className="col-span-3 px-6 py-1.5">Status</div>
                        </div>

                        {engageSenders.map((sender, index) => {
                            return (
                                <div
                                    key={sender.id}
                                    className={`grid grid-cols-12 text-sm ${
                                        index === engageSenders.length - 1
                                            ? ''
                                            : 'border-b border-gray-100'
                                    }`}
                                >
                                    <div className="col-span-3 flex items-center justify-between px-6 py-4 font-medium">
                                        {sender.value}
                                    </div>
                                    <div className="col-span-6 flex items-center justify-between px-6 py-4">
                                        {sender.email}
                                    </div>
                                    <div className="col-span-3 flex items-center justify-between px-6 py-4 font-medium">
                                        {sender.verified_at === null ? (
                                            <span className="whitespace-nowrap rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-900">
                                                Not verified
                                            </span>
                                        ) : (
                                            <span className="whitespace-nowrap rounded-full bg-green-50 px-2 py-1 text-xs text-green-500">
                                                Verified
                                            </span>
                                        )}

                                        <DropdownMenu.Root>
                                            <DropdownMenu.EllipsisTrigger className="px-2" />
                                            <DropdownMenu.Content>
                                                {sender.verified_at === null && (
                                                    <DropdownMenu.Item asChild>
                                                        <ResendVerificationEmail sender={sender} />
                                                    </DropdownMenu.Item>
                                                )}
                                                <DropdownMenu.LinkItem
                                                    to={`/accounts/${accountId}/engage/settings/senders/${sender.id}/edit`}
                                                >
                                                    Edit
                                                </DropdownMenu.LinkItem>
                                                <DropdownMenu.Item
                                                    onClick={() => {
                                                        setSelectedSender(sender);
                                                        setIsDeletingSender(true);
                                                    }}
                                                >
                                                    <div className="text-red-500">
                                                        <FontAwesomeIcon
                                                            icon={faTrash}
                                                            className="mr-2"
                                                        />
                                                        Delete
                                                    </div>
                                                </DropdownMenu.Item>
                                            </DropdownMenu.Content>
                                        </DropdownMenu.Root>
                                    </div>
                                </div>
                            );
                        })}
                    </>
                )}

                {!!engageDomains?.length && !engageSenders?.length && (
                    <div className="p-4">
                        <div className="flex flex-col items-center justify-center text-sm font-medium text-gray-600">
                            <FontAwesomeIcon
                                icon={faEnvelope}
                                className="mb-2 mt-4 rounded-full bg-gray-100 p-3"
                            />
                            No email senders found
                            <Link to={`/accounts/${accountId}/engage/settings/senders/new`}>
                                <Button variant="solid" className="mb-4 mt-3" size="sm">
                                    <FontAwesomeIcon icon={faPlus} />
                                    Add New
                                </Button>
                            </Link>
                        </div>
                    </div>
                )}
            </div>

            {!!engageSenders?.length && (
                <>
                    {isDeletingSender && (
                        <DeleteSenderDialog
                            open={isDeletingSender}
                            onOpenChange={setIsDeletingSender}
                            sender={selectedSender}
                        />
                    )}
                </>
            )}
        </>
    );
};

const ResendVerificationEmail = (props: { sender?: EngageSender }) => {
    const toast = useToast();
    const { sender } = props;
    const { accountId } = useCurrentAccount();
    const methods = useGivebutterForm();
    const { handleSubmit } = methods;
    const { mutate: submit } = useMutation({
        mutationFn: () => resendSenderVerificationEmailApi(accountId, sender?.id ?? 0),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.engageSenders(accountId),
            });
            toast.success(<>Sent verification email</>);
        },
        onError: (error: GivebutterAxiosError) => {
            toast.error(
                <>{error.response?.data.message ?? 'Unknown error sending verification email'}</>,
            );
        },
    });

    return (
        <GivebutterFormProvider {...methods}>
            <form onSubmit={handleSubmit(() => submit())}>
                <SubmitButton className="!h-auto !w-full !justify-start !border-none !bg-transparent !px-1 !py-2 !text-sm !font-medium !text-gray-900 hover:!bg-[#f2f2f27f]">
                    Resend verification
                </SubmitButton>
            </form>
        </GivebutterFormProvider>
    );
};

export default SenderSettings;
